<script lang="ts" setup>
import { ref, onMounted, computed, onBeforeMount, inject } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import axiosInstance from '@/plugins/axios';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import Editor from 'primevue/editor';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import Card from 'primevue/card';


const gF = inject('gF') as any;
const toast = useToast();
const route = useRoute();
const router = useRouter();
const staticTextKey = ref(route.params.staticTextKey);
const rootLangTexts = ref<Record<string, string>>({});
const targetLangTexts = ref<Record<string, string>>({});
const targetLangCode = ref("undefined");
const targetLangID = ref(route.params.translation);
const isSaving = ref(false); 
const isHtmlMode = ref(false); // Toggle-Variable für den HTML-Modus

// Umschalten zwischen HTML-Ansicht und WYSIWYG-Editor
const toggleHtmlMode = () => {
    isHtmlMode.value = !isHtmlMode.value;
};

const getTargetTexts = async () => {
    gF.startLoading();
    try {
        const response = await axiosInstance.get('/translations/' + targetLangID.value);
        targetLangTexts.value = response.data.staticTexts;
        targetLangCode.value = response.data.languageCode;

        // if field doesn't exist in targetLangTexts, create it with empty string
        if (targetLangTexts.value[staticTextKey.value] === undefined) {
            targetLangTexts.value[staticTextKey.value] = '';
        }
    } catch (error) {
        console.log(error);
    }
};

const getRootTexts = async () => {
    try {
        const rootTranslationId = targetLangCode.value === 'en' ? '670fad078be48456eb53b250' : '670e1e32d4634dd3c3ec7478';
        const response = await axiosInstance.get('/translations/' + rootTranslationId);
        rootLangTexts.value = response.data.staticTexts;
        gF.endLoading();

    } catch (error) {
        console.log(error);
        gF.endLoading();
    }
};

const saveTranslation = async () => {
    if (targetLangTexts.value[staticTextKey.value] !== undefined) {
        gF.startLoading();
        isSaving.value = true;
        try {
            const response = await axiosInstance.put(`/translations/updateStaticText/${targetLangID.value}/${staticTextKey.value}`, {
                value: targetLangTexts.value[staticTextKey.value]
            });
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Translation saved successfully',
                life: 3000
            });
            router.go(-1);
        } catch (error) {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Translation could not be saved: ' + error,
                life: 3000
            });
            gF.endLoading();
        } finally {
            isSaving.value = false;
            gF.endLoading();
        }
    } else {
        console.warn('No static text to save for the given key.');
        gF.endLoading();
    }
};

onBeforeMount(async () => {
    await getTargetTexts();
    await getRootTexts();


});
</script>

<template>
    <div v-if="targetLangTexts && rootLangTexts">
        <div class="row col-12 justify-content-center mb-3">
            <div class="col text-center">
                <Button class="w-30 p-button-tuev" label="Save" @click="saveTranslation()" iconPos="left" :loading="isSaving">
                    <template #icon>
                        <font-awesome-icon :icon="['fas', 'save']" />
                    </template>
                </Button>
            </div>
        </div>

        <Card class="mb-3">
            <template #title>
                Root Language
            </template>
            <template #content>
                <div v-html="rootLangTexts[staticTextKey]"></div>
            </template>
        </Card>
        
        <Card class="mb-5">
            <template #title>
            Target Language Editor
            <Button v-if="rootLangTexts[staticTextKey]?.length >= 50" @click="toggleHtmlMode" class="ml-2 p-button-tuev float-end" label="Toggle HTML"></Button>
            </template>
            <template #content>
            <div v-if="targetLangTexts[staticTextKey] !== undefined">
                <!-- Immer Textarea, wenn rootLangTexts[staticTextKey] weniger als 50 Zeichen hat -->
                <div v-if="rootLangTexts[staticTextKey]?.length < 50">
                <Textarea v-model="targetLangTexts[staticTextKey]" rows="10" class="w-100" />
                </div>
                <!-- HTML-Modus -->
                <div v-else-if="isHtmlMode">
                <Textarea v-model="targetLangTexts[staticTextKey]" rows="10" class="w-100" />
                </div>
                <!-- WYSIWYG-Modus -->
                <div v-else>
                <LimitedEditor v-model="targetLangTexts[staticTextKey]" class="w-100" />
                </div>
            </div>
            <div v-else>
                Loading...
            </div>
            </template>
        </Card>
    </div>
</template>
